import React, { useState } from "react";
import { Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaEnvelope, FaPhone, FaLock, FaShieldAlt, FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import apiClient from "../../utils/apiClient";
import useForceLightTheme from "../../hooks/useForceLightTheme";
import { useBranding } from "../../contexts/BrandingContext";
import CommonToaster from "../Common/CommonToaster";
import vcLogo from "../../Assets/Images/Logos/logo.png";
import vcFavIcon from "../../Assets/Images/Logos/simple_logo.png";
import lmsLogo from "../../Assets/Images/Logos/lms_logo.png";
import lmsFavIcon from "../../Assets/Images/Logos/lms_logo.png";
import studentImg from "../../Assets/Images/login_bg.png";
import "../Auth/Login.scss";

const isLmsCampus = () => {
  const h = window.location.hostname;
  return h.endsWith('lmscampus.com') || h === 'localhost';
};
const logo = isLmsCampus() ? lmsLogo : vcLogo;
const favIcon = isLmsCampus() ? lmsFavIcon : vcFavIcon;

const ForgotPassword = () => {
  useForceLightTheme();
  const branding = useBranding();
  const brandLogo = branding.logoUrl || logo;
  const brandFavIcon = branding.logoUrl || favIcon;
  const brandName = branding.brandName;

  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success");
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const showToastMessage = (message, type = "success") => {
    if (type === "success") {
      setSuccess(message);
      setError("");
    } else {
      setError(message);
      setSuccess("");
    }
    setToastType(type);
    setShowToast(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      if (step === 1) {
        const response = await apiClient.post("/auth/forgot-password", { email, mobile });
        if (response.data.code === 200) {
          showToastMessage("OTP sent to your email. Please check your inbox.", "success");
          setStep(2);
        } else {
          showToastMessage("Failed to send OTP. Please try again.", "error");
        }
      } else if (step === 2) {
        const response = await apiClient.post("/auth/verify-otp", { email, otp });
        if (response.data.code === 200) {
          showToastMessage("OTP verified successfully!", "success");
          setStep(3);
        } else {
          showToastMessage("Invalid OTP", "error");
        }
      } else if (step === 3) {
        if (newPassword === confirmPassword) {
          await apiClient.post("/auth/change-password", { email, newPassword });
          showToastMessage("Password changed successfully! Redirecting to login...", "success");
          setTimeout(() => navigate("/login"), 2000);
        } else {
          showToastMessage("Passwords do not match", "error");
        }
      }
    } catch (err) {
      showToastMessage(
        err?.response?.data?.message || "An error occurred. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const stepTitles = {
    1: "Reset Password",
    2: "Verify OTP",
    3: "Create New Password",
  };

  const stepSubtitles = {
    1: "Enter your details and we'll send you an OTP",
    2: "Enter the verification code sent to your email",
    3: "Choose a strong and secure new password",
  };

  return (
    <div className="auth-page">
      {/* Mobile top navbar */}
      <div className="auth-mobile-navbar">
        <Link to="/" className="auth-mobile-navbar-logo">
          <img src={brandFavIcon} alt="Logo" />
          <span>{brandName}</span>
        </Link>
        <Link to="/login" className="auth-mobile-navbar-btn">
          Sign In
        </Link>
      </div>

      <div className="auth-container">
        {/* Left: Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-content">
            <div className="auth-brand-badge">
              <img src={brandFavIcon} alt="Logo" className="auth-brand-icon" />
            </div>
            <h1 className="auth-brand-title">{brandName}</h1>
            <p className="auth-brand-subtitle">
              Don't worry — we'll help you get back into your account
            </p>
            <div className="auth-brand-illustration">
              <img src={studentImg} alt="Learning illustration" />
            </div>
            <div className="auth-brand-features">
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Secure OTP verification</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Reset in 3 simple steps</span>
              </div>
              <div className="auth-feature-item">
                <span className="auth-feature-dot" />
                <span>Back to learning in minutes</span>
              </div>
            </div>
          </div>
          <div className="auth-brand-footer">
            <img src={brandLogo} alt={brandName} className="auth-brand-logo" />
          </div>
        </div>

        {/* Right: Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-form-wrapper">
            <div className="auth-form-header">
              <h2>{stepTitles[step]}</h2>
              <p>{stepSubtitles[step]}</p>
            </div>

            {/* Step indicator */}
            <div className="auth-step-indicator">
              <span className={`auth-step-dot ${step >= 1 ? (step > 1 ? "completed" : "active") : ""}`} />
              <span className={`auth-step-dot ${step >= 2 ? (step > 2 ? "completed" : "active") : ""}`} />
              <span className={`auth-step-dot ${step >= 3 ? "active" : ""}`} />
            </div>

            <Form onSubmit={handleSubmit} className="auth-form">
              {step === 1 && (
                <>
                  <div className="auth-input-group">
                    <label>Email Address</label>
                    <div className="auth-input-wrapper">
                      <FaEnvelope className="auth-input-icon" />
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className="auth-input-group">
                    <label>Mobile Number</label>
                    <div className="auth-input-wrapper">
                      <FaPhone className="auth-input-icon" />
                      <input
                        type="tel"
                        pattern="[0-9]{10}"
                        maxLength={10}
                        placeholder="Enter your mobile number"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <div className="auth-input-group">
                  <label>Verification Code</label>
                  <div className="auth-input-wrapper">
                    <FaShieldAlt className="auth-input-icon" />
                    <input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      required
                      maxLength={6}
                      style={{ letterSpacing: "0.3em", fontWeight: 600 }}
                    />
                  </div>
                </div>
              )}

              {step === 3 && (
                <>
                  <div className="auth-input-group">
                    <label>New Password</label>
                    <div className="auth-input-wrapper">
                      <FaLock className="auth-input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={5}
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="auth-input-group">
                    <label>Confirm Password</label>
                    <div
                      className="auth-input-wrapper"
                      style={
                        confirmPassword && newPassword !== confirmPassword
                          ? { borderColor: "#ef4444" }
                          : {}
                      }
                    >
                      <FaLock className="auth-input-icon" />
                      <input
                        type={showConfirm ? "text" : "password"}
                        placeholder="Confirm new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={5}
                      />
                      <button
                        type="button"
                        className="auth-password-toggle"
                        onClick={() => setShowConfirm(!showConfirm)}
                        tabIndex={-1}
                      >
                        {showConfirm ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {confirmPassword && newPassword !== confirmPassword && (
                      <span style={{ fontSize: "0.8rem", color: "#ef4444", marginTop: 2 }}>
                        Passwords do not match
                      </span>
                    )}
                  </div>
                </>
              )}

              {(error || success) && (
                <Alert variant={error ? "danger" : "success"} className="auth-alert">
                  {error || success}
                </Alert>
              )}

              <Button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : step === 1 ? (
                  "Send OTP"
                ) : step === 2 ? (
                  "Verify OTP"
                ) : (
                  "Reset Password"
                )}
              </Button>
            </Form>

            <div className="auth-footer-text">
              Remember your password?{" "}
              <Link to="/login" className="auth-action-link">
                Sign in
              </Link>
            </div>

            <div className="auth-footer-logo">
              <img src={brandLogo} alt={brandName} />
            </div>
          </div>
        </div>
      </div>

      <CommonToaster
        show={showToast}
        message={success || error}
        type={toastType}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
};

export default ForgotPassword;
